import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useGlobalSearch from "../api/useGlobalSearch";

// Bootstrap is expected to be globally available
// If you use a bundler, import it from 'bootstrap'
const OffcanvasSearch = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const { events, recipes, portfolios, products, loading, error } = useGlobalSearch(query);

  // Remove backdrop and reset body scroll when offcanvas closes
  useEffect(() => {
    const handleHide = () => {
      document.body.classList.remove("offcanvas-backdrop", "modal-open");
      document.body.style.overflow = "";
    };

    const el = document.getElementById("offcanvasSearch");
    if (el) {
      el.addEventListener("hidden.bs.offcanvas", handleHide);
    }

    return () => {
      if (el) {
        el.removeEventListener("hidden.bs.offcanvas", handleHide);
      }
    };
  }, []);

  const closeOffcanvas = () => {
    const el = document.getElementById("offcanvasSearch");
    if (el && window.bootstrap) {
      const offcanvas = window.bootstrap.Offcanvas.getInstance(el) || new window.bootstrap.Offcanvas(el);
      offcanvas.hide();
    }
  };

  return (
    <div
      className="offcanvas offcanvas-top h-auto"
      tabIndex="-1"
      id="offcanvasSearch"
      aria-labelledby="offcanvasSearchLabel"
      data-bs-backdrop="false"
    >
      <div className="offcanvas-body">
        <form className="row g-0" onSubmit={(e) => e.preventDefault()}>
          <div className="col-12 mb-5">
            <h1 style={{ fontWeight: "bold", lineHeight: "90%", textTransform: "uppercase" }}>
              {t("search.title", "Search")}
            </h1>
          </div>

          <div className="col">
            <input
              type="search"
              className="form-control form-control-lg"
              placeholder={t("search.placeholder", "What are you looking for?")}
              aria-label="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="col-auto d-grid">
            <button type="submit" className="btn btn-black btn-lg btn-icon">
              <i className="bi bi-search"></i>
            </button>
          </div>
        </form>

        <div className="row mt-5">
          <div className="col-12">
            {loading && (
              <p className="mt-0 lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                {t("global.loading", "Loading...")}
              </p>
            )}
            {error && (
              <p className="text-danger mt-0 lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                {error}
              </p>
            )}

            {!loading && !error && (
              <>
                {(events.length || recipes.length || portfolios.length || products.length) ? (
                  <ol className="list-group list-group-separated col-12">
                    {events.map((event) => {
                      const { title, slug } = event.attributes || event;
                      return (
                        <Link
                          key={`event-${event.id}`}
                          to={`/events/${slug}`}
                          className="list-group-item d-flex justify-content-between text-black text-decoration-none"
                          onClick={closeOffcanvas}
                        >
                          <span>
                            <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                              {title}
                            </p>
                          </span>
                          <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                            {t("search.events", "Events")}
                          </p>
                        </Link>
                      );
                    })}

                    {products.map((product) => (
                      <Link
                        key={`product-${product.id}`}
                        to={`/officina/${product.slug}`}
                        className="list-group-item d-flex justify-content-between text-black text-decoration-none"
                        onClick={closeOffcanvas}
                      >
                        <span>
                          <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                            {product.name}
                          </p>
                        </span>
                        <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                          {t("search.officina", "Officina")}
                        </p>
                      </Link>
                    ))}

                    {portfolios.map((portfolio) => {
                      const { title, slug } = portfolio.attributes || portfolio;
                      return (
                        <Link
                          key={`portfolio-${portfolio.id}`}
                          to={`/photos/${slug}`}
                          className="list-group-item d-flex justify-content-between text-black text-decoration-none"
                          onClick={closeOffcanvas}
                        >
                          <span>
                            <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                              {title}
                            </p>
                          </span>
                          <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                            {t("search.portfolios", "Photos")}
                          </p>
                        </Link>
                      );
                    })}

                    {recipes.map((recipe) => {
                      const { title, slug } = recipe.attributes || recipe;
                      return (
                        <Link
                          key={`recipe-${recipe.id}`}
                          to={`/kitchen/${slug}`}
                          className="list-group-item d-flex justify-content-between text-black text-decoration-none"
                          onClick={closeOffcanvas}
                        >
                          <span>
                            <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                              {title}
                            </p>
                          </span>
                          <p className="lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                            {t("search.recipes", "Recipes")}
                          </p>
                        </Link>
                      );
                    })}
                  </ol>
                ) : (
                  query && (
                    <p className="mt-0 lead" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                      {t("search.noResults", "No results")}
                    </p>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="offcanvas-footer">
        <button
          type="button"
          className="btn btn-dark btn-lg btn-icon"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default OffcanvasSearch;
